/**
 * Cards being handled, between this table and the others at it
 * (shared/protocol/gesture.ts has the wire rules and what they keep secret).
 *
 * Out: `touch.ts` reports where a card is, in table coordinates (tableFrame.ts);
 * this thins the 60 Hz stream down to about 14 messages a second and hands it
 * to whatever sink is installed — the socket in the app, a BroadcastChannel in
 * the demo. Let-go is never thinned away.
 *
 * In: every relayed gesture goes to the subscribers (RemoteHands.tsx), outside
 * React state, because a stream of positions is exactly what should not
 * re-render a table.
 */
import type { FeltTouchMessage, FeltTouchRelay, GestureMessage, GestureRelay } from '@cards/shared'
import { getSettings } from '../settings/settings'

/** ~14 a second: smooth enough to interpolate between, and inside the
 *  server's per-socket gesture budget with room to spare. */
const SEND_EVERY_MS = 70

type Sink = (message: GestureMessage) => void

let sink: Sink | null = null
let lastSent = 0
let pending: GestureMessage | null = null
let timer = 0

export function setGestureSink(next: Sink | null): void {
  sink = next
}

/** Report a gesture. Moves are thinned; the let-go is sent at once, after the
 *  last move so it can never arrive first. */
export function shareGesture(message: GestureMessage): void {
  if (!sink || !getSettings().shareMoves) return
  if (message.phase !== 'move') {
    window.clearTimeout(timer)
    timer = 0
    pending = null
    send(message)
    return
  }
  const now = performance.now()
  if (now - lastSent >= SEND_EVERY_MS && !timer) {
    send(message)
    return
  }
  pending = message
  if (!timer) {
    timer = window.setTimeout(() => {
      timer = 0
      if (pending) send(pending)
      pending = null
    }, Math.max(0, SEND_EVERY_MS - (now - lastSent)))
  }
}

function send(message: GestureMessage): void {
  lastSent = performance.now()
  sink?.(message)
}

type Listener = (relay: GestureRelay) => void
const listeners = new Set<Listener>()

export function receiveGesture(relay: GestureRelay): void {
  for (const fn of listeners) fn(relay)
}

export function subscribeGestures(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

// ---- fingers on the felt (FeltLight.tsx) ---------------------------------

type FeltSink = (message: FeltTouchMessage) => void
let feltSink: FeltSink | null = null
let feltLast = 0
let feltPending: FeltTouchMessage | null = null
let feltTimer = 0

export function setFeltSink(next: FeltSink | null): void {
  feltSink = next
}

/** Every finger on the felt, thinned like a card's moves. All lifted is sent
 *  at once, so a glow on someone else's table goes out when yours does. */
export function shareFelt(message: FeltTouchMessage): void {
  if (!feltSink || !getSettings().shareMoves) return
  const flush = (m: FeltTouchMessage) => {
    feltLast = performance.now()
    feltSink?.(m)
  }
  if (message.fingers.length === 0) {
    window.clearTimeout(feltTimer)
    feltTimer = 0
    feltPending = null
    flush(message)
    return
  }
  const now = performance.now()
  if (now - feltLast >= SEND_EVERY_MS && !feltTimer) return flush(message)
  feltPending = message
  if (!feltTimer) {
    feltTimer = window.setTimeout(() => {
      feltTimer = 0
      if (feltPending) flush(feltPending)
      feltPending = null
    }, Math.max(0, SEND_EVERY_MS - (now - feltLast)))
  }
}

type FeltListener = (relay: FeltTouchRelay) => void
const feltListeners = new Set<FeltListener>()

export function receiveFelt(relay: FeltTouchRelay): void {
  for (const fn of feltListeners) fn(relay)
}

export function subscribeFelt(fn: FeltListener): () => void {
  feltListeners.add(fn)
  return () => {
    feltListeners.delete(fn)
  }
}
